import { Link } from 'react-router-dom';
import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import NewTabNote from '../../../components/NewTabNote';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { Button, IconArrowRight, IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles, {
  textStyle,
} from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const IMAGES = '/images/branding-styleguide-icons';

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'UI Interstate Connection Network (UI ICON)',
    cards: [
      {
        title: 'IBM Web Accessibility Checklist',
        subtitle: '[ibm.com]',
        linkUrl: REFERENCE_LINKS.ibmAccessibilityChecklist,
      },
      {
        title: 'Section 508 ICT Testing Baseline',
        subtitle: '[ictbaseline.access-board.gov]',
        linkUrl: REFERENCE_LINKS.ictTestingBaseline,
      },
      {
        title: 'Government usability testing resources',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovUsabilityTesting,
      },
    ],
  },
];

interface Example {
  image: string;
  text: string;
}

interface Guideline {
  id: string;
  title: string;
  text: string;
  link?: { label: string; url: string };
  do: Example;
  dont: Example;
}

const guidelines: Guideline[] = [
  {
    id: 'sizing',
    title: 'Sizing',
    text: 'Icons in DHCS components are designed primarily for 16px artboards, ensuring a cohesive and balanced appearance. Icons on 20px artboards can also be used when necessary, maintaining consistency across the UI. Pair 16px and 20px icons with 14pt and 16pt Public Sans for optimal harmony. For larger icons, sizes like 24px and 32px are ideal, providing versatility while preserving visual alignment within the product.',
    do: {
      image: 'screen-sizing-icons-01',
      text: 'Do use the correct icon size with Public Sans.',
    },
    dont: {
      image: 'screen-sizing-icons-02',
      text: 'Don’t alter the icon-text size ratio.',
    },
  },
  {
    id: 'color',
    title: 'Color',
    text: 'Icons should always be solid and monochromatic, ensuring they align with the same color contrast ratio standards as typography (4.5:1). The chosen color should not only meet accessibility requirements but also reflect the significance of the icon’s action, guiding users intuitively through the interface. For a deeper dive into selecting the right colors, refer to our comprehensive',
    link: { label: 'Color guidelines', url: '/branding-styleguide/colors' },
    do: {
      image: 'screen-color-guidelines-icons-01',
      text: 'Do match your icon color with your text color when pairing them.',
    },
    dont: {
      image: 'screen-color-guidelines-icons-02',
      text: 'Don’t use different colors for text and icons.',
    },
  },
  {
    id: 'alignment',
    title: 'Alignment',
    text: 'When paired with text, icons should always be center-aligned to maintain visual harmony and balance. This alignment ensures a cohesive and polished appearance, enhancing readability and overall user experience.',
    do: {
      image: 'screen-align-icons-01',
      text: 'Do center-align icons when they’re next to text.',
    },
    dont: {
      image: 'screen-align-icons-02',
      text: 'Don’t baseline-align icons to the text.',
    },
  },
];

const compositionRules = [
  'Patterns are background-only.',
  'Ripple is always cropped.',
  'Maximum two brand elements per piece.',
  'Use photography or illustration inside shapes — never both.',
  'Patterns only on a solid, brand-colored background with sufficient contrast; never a non-brand color.',
];

const patterns = [
  { image: 'program-pattern-concentric-circles', label: 'Concentric Circles' },
  { image: 'program-pattern-angled-lines', label: 'Angled Lines' },
  { image: 'program-pattern-ripple', label: 'Ripple' },
];

const elements = [
  { image: 'program-element-star', label: 'Star' },
  { image: 'program-element-sunburst', label: 'Sunburst' },
  { image: 'program-element-ripple', label: 'Ripple' },
  { image: 'program-element-call-out-bar', label: 'Call Out Bar' },
];

const renderExampleCards = ({ do: doExample, dont }: Guideline) => (
  <div className="ex-cards" key={`${doExample.image}-cards`}>
    {[
      {
        example: doExample,
        className: 'true',
        badge: 'Do',
        icon: <IconSuccess />,
        type: 'success',
      },
      { example: dont, className: 'false', badge: 'Don’t', icon: <IconDanger />, type: 'danger' },
    ].map(({ example, className, badge, icon, type }) => (
      <div className={`ex-card ${className}`} key={example.image}>
        <img
          className="ex-card-img img-responsive"
          src={`${IMAGES}/${example.image}@2x.webp`}
          width={488}
          height={248}
          alt=""
        />
        <div className="ex-card-desc">
          <BadgeStyled className={type} iconLeft={icon}>
            {badge}
          </BadgeStyled>
          <p>{example.text}</p>
        </div>
      </div>
    ))}
  </div>
);

const renderSwatches = (items: { image: string; label: string }[]) => (
  <ul className="swatches">
    {items.map(({ image, label }) => (
      <li key={image}>
        <figure>
          <img src={`${IMAGES}/${image}@2x.webp`} width={150} height={110} alt="" />
          <figcaption>{label}</figcaption>
        </figure>
      </li>
    ))}
  </ul>
);

const IconsStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Iconography & Graphics"
        breadcrumbs={[
          { label: 'Branding Styleguide', url: '/branding-styleguide' },
          { label: 'Iconography & Graphics' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="definition">
                  Definition
                </h3>
                <p className="mb-40">
                  Icons are concise visual elements that communicate meaning quickly and
                  effectively, enhancing user understanding by complementing text or standing alone
                  to represent actions, statuses, or categories. Well-designed icons provide clarity
                  and consistency, playing a vital role in creating an intuitive and visually
                  appealing user experience. While our platform doesn’t include a default icon set,
                  we offer a comprehensive library called Font Awesome Pro (Solid). You’re
                  encouraged to use this or any other preferred icon set in your projects. We
                  recommend SVG implementations for their improved accessibility, scalability, and
                  vector support, ensuring a seamless and adaptive design experience.
                </p>
                <Button
                  onClick={() =>
                    window.open('https://fontawesome.com/icons', '_blank', 'noopener,noreferrer')
                  }
                  iconAfter={<IconArrowRight />}
                  className="mb-40"
                  size="M"
                >
                  Font Awesome icons
                  <NewTabNote />
                </Button>
              </div>
              <img
                src={`${IMAGES}/icon-specimen@2x.webp`}
                alt="A grid of 66 solid icons: arrows, chevrons, check marks, a magnifying glass, a trash can, a printer, a clock, a map pin and others"
                width={1000}
                height={504}
                className="mb-40 img-responsive"
              />
              <h2 className="divider h1" id="guidelines">
                Guidelines
              </h2>
              {guidelines.map((guideline) => (
                <div key={guideline.id}>
                  <div className="page-content-item">
                    <h3 className="h3" id={guideline.id}>
                      {guideline.title}
                    </h3>
                    <p className="mb-40">
                      {guideline.text}
                      {guideline.link && (
                        <>
                          &nbsp;
                          <strong>
                            <Link className="font-sm" to={guideline.link.url} title="">
                              {guideline.link.label}
                            </Link>
                          </strong>
                          .
                        </>
                      )}
                    </p>
                  </div>
                  {guideline.id === 'sizing' && (
                    <img
                      src={`${IMAGES}/example-sizing-icons@2x.webp`}
                      alt="The same plus-circle icon at 16, 20, 24 and 32 pixels"
                      width={1000}
                      height={295}
                      className="mb-40 img-responsive"
                    />
                  )}
                  {renderExampleCards(guideline)}
                </div>
              ))}
              <section className="program-graphics">
                <h2 className="h1" id="program-graphics">
                  Program Graphics
                </h2>
                <p>
                  Beyond the functional web icons above, the Program brand has its own set of
                  graphics — patterns, elements, and illustrations. Use these only in
                  Program-branded materials, keep them in the approved brand colors, and never use
                  them in place of icons.
                </p>
                <h3 className="h3" id="patterns">
                  Patterns
                </h3>
                <h3 className="h3" id="composition">
                  Composition Rules
                </h3>
                <ul className="rules">
                  {compositionRules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
                <p className="body-md">
                  Unique patterns are visual markers for the Program brand. Background use only;
                  layer subtly over a solid brand color or a photo, keep patterns in a brand color
                  (gray or black for black-and-white materials), and pair with at most one other
                  brand element.
                </p>
                {renderSwatches(patterns)}
                <h3 className="h3" id="elements">
                  Elements
                </h3>
                {renderSwatches(elements)}
                <h3 className="h3" id="illustration">
                  Illustration
                </h3>
                <p className="body-md">
                  Illustration style: two-colour brush with hatch marks — not to be used in place of
                  icons.
                </p>
                <img
                  src={`${IMAGES}/program-illustration@2x.webp`}
                  alt="Two-colour brush illustrations of people: a person at a mailbox, figures under umbrellas, a person with a guide dog, a doctor with a patient, and workers in round badges"
                  width={1000}
                  height={400}
                  className="img-responsive"
                />
              </section>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'definition',
                    'guidelines',
                    'sizing',
                    'color',
                    'alignment',
                    'program-graphics',
                    'patterns',
                    'composition',
                    'elements',
                    'illustration',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/branding-styleguide/logos"
        previous="Logos"
        nextLink="/branding-styleguide/imagery"
        next="Imagery"
      />
    </div>
  </div>
);

const Icons = styled(IconsStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 4, 5, 6, 8, 9, 10, 11])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }

  /* The Updated frame stacks the Program Graphics section on a 24px rhythm, sub-headings in Neutral/text-weak. */
  .page-content .program-graphics {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2.5rem;

    > * {
      margin: 0;
    }

    .h3 {
      color: ${({ theme }) => theme.neutralTextWeak};
    }

    .body-md,
    .rules {
      ${textStyle('paragraphMedium')}
    }

    .rules li {
      margin-bottom: 0.5rem;

      &::before {
        content: '• ';
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    .swatches {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;

      li {
        margin: 0;
      }

      figure {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin: 0;
      }

      img {
        display: block;
      }

      figcaption {
        ${textStyle('h5Sb')}
        color: ${({ theme }) => theme.neutralText};
      }
    }
  }
`;

export default Icons;
